import React, { useState, useEffect } from 'react';
import { TableLoader } from '../../../components/Common/Loader';
import { CustomDropdown } from '../../../components/Common/CustomDropdown';
import { Plus, Search, Edit2, X, Check, ArrowLeft, Eye } from 'lucide-react';
import { getCitiesAPI, createCityAPI, updateCityAPI, getTerritoriesAPI } from '../../../api/locations';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

export interface CityTerritory {
  id: number;
  zoneId?: number;
  sapCode?: string | null;
  name: string;
  zone?: {
    id: number;
    name: string;
  };
}

export interface CityItem {
  id: number;
  name: string;
  sapCode?: string | null;
  territoryId?: number;
  status?: number | string;
  territory?: CityTerritory;
}

const initialCitiesMock: CityItem[] = [
  { id: 9, territoryId: 4, sapCode: null, name: 'Aurangabad', territory: { id: 4, name: 'Aurangabad', zone: { id: 1, name: 'WEST' } } },
  { id: 12, territoryId: 6, sapCode: null, name: 'Bangalore', territory: { id: 6, name: 'Bangalore', zone: { id: 2, name: 'SOUTH' } } },
  { id: 13, territoryId: 7, sapCode: null, name: 'Chennai', territory: { id: 7, name: 'Chennai', zone: { id: 2, name: 'SOUTH' } } },
  { id: 5, territoryId: 3, sapCode: null, name: 'Pune City', territory: { id: 3, name: 'Pune', zone: { id: 1, name: 'WEST' } } },
];

export const CitiesList: React.FC = () => {
  const [cities, setCities] = useState<CityItem[]>(initialCitiesMock);
  const [territories, setTerritories] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<CityItem | null>(null);
  const [viewingCity, setViewingCity] = useState<CityItem | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [sapCode, setSapCode] = useState('');
  const [territoryId, setTerritoryId] = useState<number>(3);

  const fetchCities = async () => {
    try {
      setLoading(true);
      const res = await getCitiesAPI();
      const rawList = res?.data || res?.cities || (Array.isArray(res) ? res : null);
      if (Array.isArray(rawList)) {
        setCities(rawList);
      }
    } catch (err) {
      console.warn('Using fallback data for cities:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTerritories = async () => {
    try {
      const res = await getTerritoriesAPI();
      const rawList = res?.data || res?.territories || (Array.isArray(res) ? res : []);
      if (Array.isArray(rawList) && rawList.length > 0) {
        setTerritories(rawList);
      }
    } catch (err) {
      console.warn('Error fetching territories for dropdown:', err);
    }
  };

  useEffect(() => {
    fetchCities();
    fetchTerritories();
  }, []);

  const handleOpenAddModal = () => {
    setEditingCity(null);
    setName('');
    setSapCode('');
    setTerritoryId(territories.length > 0 ? territories[0].id : 3);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (city: CityItem) => {
    setEditingCity(city);
    setName(city.name);
    setSapCode(city.sapCode || '');
    setTerritoryId(city.territoryId || city.territory?.id || (territories.length > 0 ? territories[0].id : 3));
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      if (editingCity) {
        await updateCityAPI(editingCity.id, {
          name,
          territoryId,
          sapCode: sapCode.trim() || undefined,
        });
      } else {
        await createCityAPI({
          name,
          territoryId,
          sapCode: sapCode.trim() || undefined,
        });
      }
      await fetchCities();
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Error saving city:', err);
      alert(err?.message || 'Failed to save city');
    }
  };

  const filteredCities = cities.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.sapCode && c.sapCode.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (c.territory && c.territory.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleExportCSV = () => {
    const headers = ['ID', 'City Name', 'Territory', 'SAP Code', 'Status'];
    const rows = filteredCities.map((c) => [
      c.id,
      c.name,
      c.territory?.name || '',
      c.sapCode || '',
      c.status === 1 || c.status === '1' || c.status === 'Active' ? 'Active' : 'Inactive',
    ]);
    exportToCSV('cities_list', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Cities Management
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-md shadow-[#C1272D]/20 flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Add City</span>
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Controls Bar */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-base font-bold text-slate-800">All Cities</h3>

          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search cities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 focus:outline-none focus:border-[#C1272D]"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                <th className="py-3.5 px-6">ID</th>
                <th className="py-3.5 px-6">City Name</th>
                <th className="py-3.5 px-6">Territory</th>
                <th className="py-3.5 px-6">Zone</th>
                <th className="py-3.5 px-6 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
              {filteredCities.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-400">{c.id}</td>
                  <td className="py-4 px-6 font-bold text-slate-800">{c.name}</td>
                  <td className="py-4 px-6 font-medium text-slate-700">
                    {c.territory?.name || territories.find(t => t.id === c.territoryId)?.name || (c.territoryId ? `Territory ${c.territoryId}` : '-')}
                  </td>
                  <td className="py-4 px-6 font-semibold text-slate-700">
                    <span className="px-2.5 py-1 bg-amber-100/70 text-amber-800 rounded-md text-[11px] font-bold">
                      {c.territory?.zone?.name || 'WEST'}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setViewingCity(c)}
                        className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenEditModal(c)}
                        className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading ? (
                <TableLoader colSpan={5} text="Loading..." />
              ) : filteredCities.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400 font-medium">
                    No cities found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* View City Modal */}
      {viewingCity && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-extrabold text-slate-800">{viewingCity.name}</h3>
                <p className="text-xs font-mono font-medium text-slate-400">City ID: {viewingCity.id}</p>
              </div>
              <button onClick={() => setViewingCity(null)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">City Name</span>
                  <p className="text-sm font-bold text-slate-900">{viewingCity.name}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">SAP Code</span>
                  <p className="text-sm font-bold text-slate-900">{viewingCity.sapCode || '—'}</p>
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Territory</span>
                <p className="text-sm font-bold text-slate-900">
                  {viewingCity.territory?.name || territories.find(t => t.id === viewingCity.territoryId)?.name || (viewingCity.territoryId ? `Territory ${viewingCity.territoryId}` : '-')}
                </p>
              </div>
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Zone</span>
                <span className="px-2.5 py-1 bg-amber-100/70 text-amber-800 rounded-md text-[11px] font-bold">{viewingCity.territory?.zone?.name || 'WEST'}</span>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button onClick={() => setViewingCity(null)} className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <h3 className="text-lg font-extrabold text-slate-800">
                {editingCity ? 'Edit City' : 'Add New City'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="space-y-1">
                <label className="font-bold text-slate-700">City Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Pune City"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">SAP Code</label>
                <input
                  type="text"
                  placeholder="e.g. PNE"
                  value={sapCode}
                  onChange={(e) => setSapCode(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 focus:outline-none focus:border-[#C1272D]"
                />
              </div>

              <div className="space-y-1">
                <CustomDropdown
                  label="Territory *"
                  placeholder="Select Territory"
                  options={
                    territories.length > 0
                      ? territories.map((t) => ({ id: t.id, name: t.name }))
                      : ['Pune', 'Aurangabad', 'Bangalore', 'Chennai']
                  }
                  value={
                    territories.find((t) => t.id === territoryId)?.name ||
                    (editingCity?.territory?.name) ||
                    ''
                  }
                  onChange={(selectedName: string) => {
                    const matched = territories.find((t) => t.name === selectedName);
                    if (matched) {
                      setTerritoryId(matched.id);
                    }
                  }}
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold rounded-xl flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingCity ? 'Save Changes' : 'Add City'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitiesList;
